import { motion } from "framer-motion";
import { Building2, ShieldCheck, MapPin } from "lucide-react";

const INSTITUTIONS = [
  {
    name: "Hospital São Nicolau",
    location: "Cliente Áxis",
    tag: "Instituição atendida",
  },
];

// Marquee list — the same set duplicated for infinite loop
const CLIENTS_MARQUEE = [
  "Hospital São Nicolau",
  "Dr. Paulo Safatle",
  "Michely Melo",
  "Angelo",
  "Hospital São Nicolau",
  "Dr. Paulo Safatle",
  "Michely Melo",
  "Angelo",
];

export default function Institutions() {
  return (
    <section
      data-testid="institutions-section"
      className="relative bg-white py-24 lg:py-32 overflow-hidden border-t border-gray-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16"
        >
          <div className="lg:col-span-6">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#3B82F6] mb-4">
              /05 · Instituições atendidas
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
              Quem confia na{" "}
              <span className="text-[#1E3A8A]">Áxis.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
              Hospitais, profissionais e gestores que escolheram a Áxis como
              parceira tecnológica para transformar suas operações.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Featured hospital card */}
          {INSTITUTIONS.map((inst, i) => (
            <motion.article
              key={inst.name}
              data-testid={`institution-${i}`}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 relative bg-[#0B1220] text-white p-10 lg:p-12 overflow-hidden group"
            >
              <div className="grain absolute inset-0 opacity-70" aria-hidden />
              <div
                aria-hidden
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#3B82F6]/10 blur-3xl"
              />

              <div className="relative">
                <span className="inline-flex items-center gap-2 px-3 py-1 border border-[#3B82F6]/30 text-[#3B82F6] font-mono-axis text-[10px] uppercase tracking-widest">
                  <span className="relative w-1.5 h-1.5 rounded-full bg-[#3B82F6] text-[#3B82F6] live-dot" />
                  {inst.tag}
                </span>

                <div className="mt-10 flex items-start gap-4">
                  <div className="w-14 h-14 bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#3B82F6] transition-colors">
                    <Building2
                      className="w-6 h-6 text-[#3B82F6]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-3xl lg:text-4xl font-semibold tracking-tighter text-white leading-none">
                      {inst.name}
                    </h3>
                    <p className="mt-2 flex items-center gap-2 text-sm text-gray-400 font-mono-axis uppercase tracking-widest text-xs">
                      <MapPin className="w-3 h-3" />
                      {inst.location}
                    </p>
                  </div>
                </div>

                <p className="mt-10 text-base text-gray-300 leading-relaxed max-w-md">
                  Parceria tecnológica com foco em integração ao ERP Tasy,
                  automação de processos hospitalares e inteligência gerencial
                  em tempo real.
                </p>

                <div className="mt-10 pt-8 border-t border-white/10 flex items-center gap-3 text-xs font-mono-axis uppercase tracking-widest text-gray-400">
                  <ShieldCheck className="w-4 h-4 text-[#3B82F6]" />
                  Parceria consolidada · LGPD compliant
                </div>
              </div>
            </motion.article>
          ))}

          {/* Individual people using the platforms */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <p className="font-mono-axis text-xs uppercase tracking-widest text-gray-500 mb-6">
              Profissionais que utilizam nossos sistemas
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  name: "Dr. Paulo Safatle",
                  role: "Diretor",
                  initials: "PS",
                },
                {
                  name: "Michely Melo",
                  role: "Coordenação",
                  initials: "MM",
                },
                {
                  name: "Angelo",
                  role: "Gestão",
                  initials: "AN",
                },
              ].map((p, i) => (
                <motion.div
                  key={p.name}
                  data-testid={`person-${p.initials.toLowerCase()}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.6,
                    delay: 0.1 * i,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileHover={{ y: -4 }}
                  className="relative bg-white border border-gray-200 p-6 group hover:border-[#3B82F6] transition-colors"
                >
                  <span
                    aria-hidden
                    className="absolute top-0 left-0 h-[2px] w-0 bg-[#3B82F6] transition-[width] duration-300 group-hover:w-full"
                  />
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#1E3A8A] text-white flex items-center justify-center font-display font-semibold tracking-tight">
                    {p.initials}
                  </div>
                  <p className="mt-5 font-display text-lg font-medium text-[#111827] tracking-tight leading-snug">
                    {p.name}
                  </p>
                  <p className="mt-1 text-xs font-mono-axis uppercase tracking-widest text-gray-500">
                    {p.role}
                  </p>
                  <p className="mt-4 text-xs text-gray-500 pt-4 border-t border-gray-100">
                    Hospital São Nicolau
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Growing base */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 bg-[#F9FAFB] border border-gray-200 p-6 flex items-center justify-between gap-4"
            >
              <div>
                <p className="font-display text-2xl font-semibold text-[#111827] tracking-tighter">
                  Base em expansão
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Novas instituições sendo integradas ao ecossistema Áxis.
                </p>
              </div>
              <div className="hidden sm:flex items-center -space-x-3">
                {["PS", "MM", "AN"].map((i, idx) => (
                  <span
                    key={i}
                    className="w-10 h-10 rounded-full bg-white border-2 border-white shadow-sm bg-gradient-to-br from-[#3B82F6] to-[#1E3A8A] text-white text-xs font-display font-semibold flex items-center justify-center"
                    style={{ zIndex: 10 - idx }}
                  >
                    {i}
                  </span>
                ))}
                <span className="w-10 h-10 rounded-full bg-[#111827] border-2 border-white shadow-sm text-white text-xs font-display font-semibold flex items-center justify-center">
                  +
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-20 lg:mt-24 border-y border-gray-200 bg-[#F9FAFB] py-6 overflow-hidden">
        <div className="flex marquee whitespace-nowrap gap-16 items-center">
          {CLIENTS_MARQUEE.map((c, i) => (
            <span
              key={i}
              className="font-display text-2xl lg:text-3xl font-medium text-gray-300 tracking-tight flex items-center gap-16"
            >
              {c}
              <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
